import { useMemo, useState } from "react"

import { MAIN_PORTAL_INSTANCE_ID } from "@productlane/lib/src/portal-instance"
import { Button, Dialog } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { Field, Picker } from "../helpCenter/move-to-portal-dialog"

const MAIN_PORTAL_NAME = "Main portal"

const plural = (count: number, word: string) =>
	`${count} ${word}${count === 1 ? "" : "s"}`

/** Moves every project and issue on one requests portal to another portal. */
export function MoveRequestsToPortalDialog({
	source,
	isOpen,
	onClose,
}: {
	source: { id: string | null; name: string }
	isOpen: boolean
	onClose: () => void
}) {
	const workspace = useZeroWorkspace()
	const portals = useMemo(
		() => [
			{ id: null as string | null, name: MAIN_PORTAL_NAME },
			...(workspace?.portalInstances ?? []).map((i) => ({
				id: i.id as string | null,
				name: i.name,
			})),
		],
		[workspace?.portalInstances],
	)
	const destinations = portals.filter((p) => p.id !== source.id)
	const [targetId, setTargetId] = useState<string | null | undefined>(
		destinations[0]?.id,
	)
	const portalName = (id: string | null | undefined) =>
		portals.find((p) => p.id === id)?.name ?? MAIN_PORTAL_NAME

	const counts = api.workspace.countRoadmapItemsOnPortal.useQuery(
		{ fromPortalId: source.id, toPortalId: targetId ?? null },
		{ enabled: isOpen && targetId !== undefined },
	)
	const move = api.workspace.moveRoadmapItemsToPortal.useMutation()

	const total =
		(counts.data?.projectsMoved ?? 0) + (counts.data?.issuesMoved ?? 0)
	const canMove =
		targetId !== undefined && counts.isSuccess && total > 0 && !move.isLoading

	const onMove = async () => {
		if (!canMove) return
		try {
			const result = await move.mutateAsync({
				fromPortalId: source.id,
				toPortalId: targetId,
			})
			analytics.track("roadmap-move-to-portal", {
				from: source.id ?? "root",
				to: targetId ?? "root",
				projects: result.projectsMoved,
				issues: result.issuesMoved,
			})
			toast.success({
				title: `Moved ${plural(result.projectsMoved + result.issuesMoved, "request")} to ${portalName(targetId)}.`,
			})
			onClose()
		} catch (error) {
			toast.error({
				title: "Couldn't move this to another portal.",
				description: error instanceof Error ? error.message : undefined,
			})
		}
	}

	return (
		<Dialog
			className="max-w-96 p-4"
			isOpen={isOpen}
			onOpenChange={(state) => {
				if (state === false) onClose()
			}}
		>
			<h2 className="mb-4 text-lg font-bold">{`Move requests from "${source.name}"`}</h2>

			<div className="space-y-3">
				<Field label="Portal">
					<Picker
						label={portalName(targetId)}
						placeholder="Switch portal..."
						empty="No portals."
						options={destinations.map((p) => ({
							key: p.id ?? "__root__",
							value: p.name,
							label: p.name,
							onSelect: () => setTargetId(p.id),
						}))}
					/>
				</Field>
			</div>

			<p className="mt-3 min-h-4 text-xs text-tertiary">
				{counts.data &&
					`Moves its ${plural(counts.data.projectsMoved, "project")} and ${plural(counts.data.issuesMoved, "issue")}. Requests on other portals stay there too.`}
			</p>

			<div className="mt-4 flex justify-end gap-2">
				<Button variant="outline" onClick={onClose}>
					Cancel
				</Button>
				<Button onClick={onMove} disabled={!canMove} isLoading={move.isLoading}>
					Move
				</Button>
			</div>
		</Dialog>
	)
}

/** The portal an item is on (null for Main), and whether that is its only one. */
export function currentRequestPortal(portalInstanceIds: readonly string[]) {
	const named = portalInstanceIds.filter((id) => id !== MAIN_PORTAL_INSTANCE_ID)
	const onMain =
		portalInstanceIds.length === 0 ||
		portalInstanceIds.includes(MAIN_PORTAL_INSTANCE_ID)
	return {
		currentId: onMain ? null : (named[0] ?? null),
		isOnOnePortal: (onMain ? 1 : 0) + named.length <= 1,
	}
}

/** Moves one project or issue onto a single portal, off every other one. */
export function MoveRequestToPortalDialog({
	request,
	isOpen,
	onClose,
}: {
	request: {
		type: "project" | "issue"
		id: string
		name: string
		portalInstanceIds: readonly string[]
	}
	isOpen: boolean
	onClose: () => void
}) {
	const zero = useZero()
	const workspace = useZeroWorkspace()
	const portals = useMemo(
		() => [
			{ id: null as string | null, name: MAIN_PORTAL_NAME },
			...(workspace?.portalInstances ?? []).map((i) => ({
				id: i.id as string | null,
				name: i.name,
			})),
		],
		[workspace?.portalInstances],
	)
	const { currentId, isOnOnePortal } = currentRequestPortal(
		request.portalInstanceIds,
	)
	const [targetId, setTargetId] = useState<string | null>(currentId)
	const [isMoving, setIsMoving] = useState(false)
	const portalName = (id: string | null) =>
		portals.find((p) => p.id === id)?.name ?? MAIN_PORTAL_NAME

	const canMove = !(isOnOnePortal && targetId === currentId) && !isMoving

	const onMove = async () => {
		if (!canMove) return
		setIsMoving(true)
		const patch = {
			id: request.id,
			portalInstanceIds: targetId === null ? [] : [targetId],
		}
		try {
			await mutateOk(
				zero.mutate(
					request.type === "project"
						? mutators.projects.update(patch)
						: mutators.issues.update(patch),
				),
			)
			analytics.track("roadmap-item-move-to-portal", {
				type: request.type,
				id: request.id,
				to: targetId ?? "root",
			})
			toast.success({
				title: `Moved "${request.name}" to ${portalName(targetId)}.`,
			})
			onClose()
		} catch (error) {
			toast.error({
				title: "Couldn't move this to another portal.",
				description: error instanceof Error ? error.message : undefined,
			})
		} finally {
			setIsMoving(false)
		}
	}

	return (
		<Dialog
			className="max-w-96 p-4"
			isOpen={isOpen}
			onOpenChange={(state) => {
				if (state === false) onClose()
			}}
		>
			<h2 className="mb-4 text-lg font-bold">{`Move "${request.name}"`}</h2>

			<div className="space-y-3">
				<Field label="Portal">
					<Picker
						label={portalName(targetId)}
						placeholder="Switch portal..."
						empty="No portals."
						options={portals.map((p) => ({
							key: p.id ?? "__root__",
							value: p.name,
							label: p.name,
							onSelect: () => setTargetId(p.id),
						}))}
					/>
				</Field>
			</div>

			<div className="mt-4 flex justify-end gap-2">
				<Button variant="outline" onClick={onClose}>
					Cancel
				</Button>
				<Button onClick={onMove} disabled={!canMove} isLoading={isMoving}>
					Move
				</Button>
			</div>
		</Dialog>
	)
}
